import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { Document } from '../../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';

interface DocumentDetailsDialogProps {
  open: boolean;
  loading: boolean;
  document: Document | null;
  onOpenChange: (open: boolean) => void;
  onOpenFull: () => void;
  onOpenSource?: () => void;
}

export function DocumentDetailsDialog({
  open,
  loading,
  document,
  onOpenChange,
  onOpenFull,
  onOpenSource,
}: DocumentDetailsDialogProps) {
  const hasSource = !!document?.source_url && !!onOpenSource;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-border bg-muted/30 space-y-0">
          <DialogTitle className="text-sm font-semibold text-foreground">
            Document details
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 min-h-0 max-h-[calc(85vh-4rem)]">
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Spinner className="h-8 w-8 text-muted-foreground" />
              </div>
            ) : document ? (
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {document.tags?.[0]?.label && (
                    <span className="font-medium text-foreground/80">
                      {document.tags[0].label}
                    </span>
                  )}
                  {document.category && <span>{document.category}</span>}
                  <span>
                    Created{' '}
                    {new Date(document.created_at).toLocaleDateString(
                      undefined,
                      {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }
                    )}
                  </span>
                  {document.updated_at && (
                    <span>
                      Updated{' '}
                      {new Date(document.updated_at).toLocaleDateString(
                        undefined,
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        }
                      )}
                    </span>
                  )}
                </div>
                <h3
                  className="text-lg font-semibold text-foreground leading-tight truncate min-w-0"
                  title={document.title}
                >
                  {document.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 h-9"
                    onClick={onOpenFull}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open full document
                  </Button>
                  {hasSource && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 h-9"
                      onClick={onOpenSource}
                    >
                      <ExternalLink className="h-4 w-4" />
                      View source
                    </Button>
                  )}
                </div>
                <Separator />
                <div className="prose prose-neutral dark:prose-invert prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/95">
                    {document.content || 'No content available.'}
                  </div>
                </div>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Could not load document.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

